import React, { useState } from 'react';
import { useFollowUp } from '../../hooks/useFollowUp';
import FollowUpPrompt from './FollowUpPrompt';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';

/**
 * FollowUpEngine
 *
 * Dashboard panel surfacing smart follow-up suggestions + normal-priority
 * notifications as a unified mixed feed.
 */
export default function FollowUpEngine({
  tasks = [],
  googleEvents = [],
  icalEvents = [],
  outlookEvents = [],
  onOpenTask,
  onMarkSent,
  normalNotifications = [],
  onDismissNotification,
  onNavigate,
}) {
  const { prompts, dismiss, snoozeUntilTomorrow, recalculate, shouldShow } =
    useFollowUp({ tasks, googleEvents, icalEvents, outlookEvents });

  const [collapsed, setCollapsed] = useState(false);

  // Map notification type → navigation target
  function notifTarget(n) {
    const t = n.type || '';
    if (t.startsWith('leave_')) return { view: 'TeamHR' };
    if (t.startsWith('invoice_')) return { view: 'Invoicing' };
    if (t.startsWith('task_')) return { view: 'Tasks' };
    if (t === 'family_chat_message') return { view: 'FamilyChat' };
    return null;
  }

  // Merge AI prompts with normal-priority notification items
  const notifPrompts = normalNotifications.map(n => ({
    id: `notif-${n.id}`,
    _notifId: n.id,
    type: 'notification',
    priority: n.priority,
    label: n.title,
    detail: n.message,
    createdAt: n.createdAt,
    target: notifTarget(n),
  }));

  const allPrompts = [...notifPrompts, ...prompts];

  const handleDismiss = (prompt) => {
    if (prompt.type === 'notification' && onDismissNotification) {
      onDismissNotification(prompt._notifId);
    } else {
      dismiss(prompt.id);
    }
  };

  return (
    <div className="dashboard-box" style={{
      overflow: 'hidden',
      fontFamily: "'Raleway', sans-serif",
      minHeight: '160px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Smart Follow-ups</h3>
          <span style={{ fontSize: '0.85rem', color: '#999' }}>
            {allPrompts.length} suggestion{allPrompts.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={recalculate}
            title="Refresh"
            style={{
              background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#999',
              padding: '0.25rem 0.6rem', fontFamily: "'Raleway', sans-serif",
            }}
          >
            Refresh
          </button>
          {allPrompts.length > 0 && (
            <button
              onClick={() => setCollapsed(c => !c)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#999', fontSize: '1rem', lineHeight: 1, padding: '0.1rem 0.3rem',
              }}
            >
              {collapsed ? '▾' : '▴'}
            </button>
          )}
        </div>
      </div>

      {/* Prompt list */}
      {allPrompts.length === 0 ? (
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#aaa' }}>
          No follow-ups right now — you're all caught up.
        </p>
      ) : !collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {allPrompts.map(prompt =>
            prompt.type === 'notification' ? (
              <div
                key={prompt.id}
                onClick={prompt.target && onNavigate ? () => onNavigate(prompt.target.view) : undefined}
                title={prompt.target ? 'Click to open' : undefined}
                style={{
                  padding: '0.75rem', borderRadius: 8,
                  border: `1px solid ${BORDER}`, background: '#fafbff',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem',
                  cursor: prompt.target && onNavigate ? 'pointer' : 'default',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={prompt.target && onNavigate ? e => { e.currentTarget.style.background = '#f0f3ff'; } : undefined}
                onMouseLeave={prompt.target && onNavigate ? e => { e.currentTarget.style.background = '#fafbff'; } : undefined}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: NAVY, marginBottom: '0.2rem' }}>
                    {prompt.label}
                    {prompt.target && onNavigate && (
                      <span style={{ marginLeft: 5, fontSize: '0.75rem', color: '#bbb' }}>↗</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#555' }}>{prompt.detail}</div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); handleDismiss(prompt); }}
                  title="Dismiss"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#bbb', fontSize: '1.1rem', lineHeight: 1, flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <FollowUpPrompt
                key={prompt.id}
                prompt={prompt}
                onDismiss={() => handleDismiss(prompt)}
                onSnooze={() => snoozeUntilTomorrow(prompt.id)}
                onMarkSent={onMarkSent}
                onOpenTask={onOpenTask}
                onNavigate={onNavigate}
              />
            )
          )}

          <p style={{
            margin: '0.25rem 0 0',
            fontSize: '0.85rem', color: '#999',
            textAlign: 'center', fontStyle: 'italic',
          }}>
            Aidey will never send anything without your approval.
          </p>
        </div>
      )}
    </div>
  );
}
